// view/tabs.js — tabs over server-rendered panels (plan documents_admin-search, phase 7c: "Admin-Filter" /
// "Frontpage-Suche" in the documents.php filter). Generic markup contract:
//
//   <div data-tabs data-tabs-input="filter_mode">            container; optional: name of a hidden input it holds
//     <div role="tablist">
//       <button type="button" role="tab" aria-selected="true" aria-controls="panelA" data-tab-value="">A</button>
//       <button type="button" role="tab" aria-selected="false" tabindex="-1" aria-controls="panelB" data-tab-value="b">B</button>
//     </div>
//     <input type="hidden" name="filter_mode" value="">
//     <div role="tabpanel" id="panelA">…</div>
//     <div role="tabpanel" id="panelB" hidden>…</div>
//   </div>
//
// A click (or Left / Right / Home / End on a tab) selects a tab: aria-selected + tabindex on the tabs, hidden on the
// panels, and the hidden input gets the tab's data-tab-value. Switching only shows the panel — the form is sent by its
// own buttons. Delegated on the root, so markup that is replaced later (ajax filter table) needs no setup.
//
// Optional tabs (documents filter: the superuser tabs behind "+", boss 2026-10-13): tabs with data-tab-optional are
// hidden while the bar is closed; a toggle button in the tablist opens / closes them — the server renders the state
// (open when an optional tab is the selected one):
//
//       <button type="button" role="tab" data-tab-optional hidden …>C</button>
//       <button type="button" data-tabs-toggle aria-expanded="false" data-label-open="…" data-label-close="…">+</button>
//
// Closing while an optional tab is selected selects the first tab that stays visible. Arrow keys skip hidden tabs.
//
// Returns { destroy }.

import { createDisposers } from '../core/disposers.js';

const TAB_SELECTOR = '[data-tabs] [role="tab"]';
const TOGGLE_SELECTOR = '[data-tabs] [data-tabs-toggle]';

export function createTabs({ root = document } = {}) {
    const disposers = createDisposers();
    const doc = root.ownerDocument || root;

    const allTabsOf = (container) => Array.from(container.querySelectorAll('[role="tab"]'))
        .filter((tab) => tab.closest('[data-tabs]') === container);
    const tabsOf = (container) => allTabsOf(container).filter((tab) => !tab.hidden);

    const select = (tab) => {
        const container = tab.closest('[data-tabs]');
        for (const other of allTabsOf(container)) {
            const chosen = other === tab;
            other.setAttribute('aria-selected', chosen ? 'true' : 'false');
            other.tabIndex = chosen ? 0 : -1;
            const panel = doc.getElementById(other.getAttribute('aria-controls'));
            if (panel) {
                panel.hidden = !chosen;
            } else {
                console.error('[tabs] no panel for tab', other);   // markup error, visible instead of a dead tab
            }
        }
        const inputName = container.dataset.tabsInput;
        if (inputName) {
            const input = Array.from(container.querySelectorAll('input[type="hidden"]')).find((el) => el.name === inputName);
            if (input) {
                input.value = tab.dataset.tabValue ?? '';
            } else {
                console.error('[tabs] no hidden input', inputName, 'in', container);
            }
        }
    };

    // opens / closes the optional tabs of the toggle's bar
    const toggle = (button) => {
        const container = button.closest('[data-tabs]');
        const open = button.getAttribute('aria-expanded') !== 'true';
        const optional = allTabsOf(container).filter((tab) => tab.hasAttribute('data-tab-optional'));
        for (const tab of optional) {
            tab.hidden = !open;
        }
        button.setAttribute('aria-expanded', String(open));
        button.textContent = open ? '−' : '+';
        const label = open ? button.dataset.labelClose : button.dataset.labelOpen;
        if (label) {
            button.setAttribute('aria-label', label);
            button.title = label;
        }
        const selected = optional.find((tab) => tab.getAttribute('aria-selected') === 'true');
        if (!open && selected) {
            const first = tabsOf(container)[0];
            if (first) {
                select(first);
            } else {
                console.error('[tabs] closing hides every tab', container);   // markup error: no tab stays visible
            }
        }
    };

    const onClick = (ev) => {
        const button = ev.target.closest(TOGGLE_SELECTOR);
        if (button && root.contains(button)) {
            toggle(button);
            return;
        }
        const tab = ev.target.closest(TAB_SELECTOR);
        if (tab && root.contains(tab)) {
            select(tab);
        }
    };

    const onKeyDown = (ev) => {
        const tab = ev.target.closest(TAB_SELECTOR);
        if (!tab || !root.contains(tab) || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(ev.key)) {
            return;
        }
        const tabs = tabsOf(tab.closest('[data-tabs]'));
        const at = tabs.indexOf(tab);
        const next = { ArrowLeft: at - 1, ArrowRight: at + 1, Home: 0, End: tabs.length - 1 }[ev.key];
        const target = tabs[(next + tabs.length) % tabs.length];
        ev.preventDefault();
        select(target);
        target.focus();
    };

    root.addEventListener('click', onClick);
    root.addEventListener('keydown', onKeyDown);
    disposers.add(() => root.removeEventListener('click', onClick), () => root.removeEventListener('keydown', onKeyDown));

    return { destroy: () => disposers.dispose() };
}
